import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, Clock, Users, FileText, Plus, AlertTriangle, CheckCircle, Search, X, Upload } from 'lucide-react';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import AddParticipantModal from './AddParticipantModal';
import FileUploadModal from './FileUploadModal';
import DocumentUploadModal from './MyTask/DocumentUploadModal';
import AgendaUploadModal from './AgendaUploadModal';
import CustomRecurrenceModal from './CustomRecurrenceModal';
import QuillEditor from './ui/QuillEditor';

import { ConfirmDialog, useConfirmDialog } from './ui/confirm-dialog';
import {
  findConflictingParticipants,
  getParticipantAvailability,
  generateConflictMessage,
  formatTime
} from '../utils/meetingValidation';
import { meetingAPI, userAPI } from '../Services/apiService';
import FormField from './FormField';
import { validateMeetingTitle, validateLocation, validateMeetingType, validateDate, validateTimeRange, validateStartTime, validateEndTime, validateParticipants, validateAgenda } from '../utils/formValidation';

const CreateMeetingModal = ({ open, onClose, onSubmit, users = [], isLoadingUsers = false }) => {
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [ongoingMeetings, setOngoingMeetings] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [emailForNewParticipant, setEmailForNewParticipant] = useState('');
  const [allUsers, setAllUsers] = useState(users); // Includes newly created vendors
  const [frequentParticipants, setFrequentParticipants] = useState([]); // Frequently used participants
  const [formData, setFormData] = useState({
    title: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    location: '',
    meeting_type: 'in-person',
    recurrence: 'none',
    custom_recurrence: null,
    participant_ids: [],
    agenda: ''
  });

  // Close confirmation dialog
  const closeConfirm = useConfirmDialog();


  // Check if form has data (is dirty)
  const isFormDirty = () => {
    // For agenda, strip HTML tags to check if there's actual content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.agenda;
    const agendaText = (tempDiv.textContent || tempDiv.innerText || '').trim();

    return formData.title.trim() !== '' ||
      formData.scheduled_date !== '' ||
      formData.start_time !== '' ||
      formData.end_time !== '' ||
      formData.location.trim() !== '' ||
      agendaText !== '' ||
      formData.participant_ids.length > 0 ||
      agendaFiles.length > 0;
  };


  // Reset form data
  const resetForm = () => {
    setFormData({
      title: '',
      scheduled_date: '',
      start_time: '',
      end_time: '',
      location: '',
      meeting_type: 'in-person',
      recurrence: 'none',
      custom_recurrence: null,
      participant_ids: [],
      agenda: ''
    });
    // Reset validation states
    setTitleValidation({ isValid: true, message: '' });
    setLocationValidation({ isValid: true, message: '' });
    setMeetingTypeValidation({ isValid: true, message: '' });
    setDateValidation({ isValid: true, message: '' });
    setStartTimeValidation({ isValid: true, message: '' });
    setEndTimeValidation({ isValid: true, message: '' });
    setParticipantsValidation({ isValid: true, message: '' });
    setAgendaValidation({ isValid: true, message: '' });
    // Reset other states
    setSearchQuery('');
    setConflicts([]);
    setAgendaFiles([]);
  };

  // Handle modal close with confirmation
  const handleModalClose = (openState) => {
    if (!openState && isFormDirty()) {
      // Trying to close with unsaved changes
      closeConfirm.showConfirm(() => {
        resetForm();
        onClose(false);
      });
    } else {
      // No changes or opening modal
      if (!openState) {
        resetForm();
      }
      onClose(openState);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    if (isFormDirty()) {
      // Show confirmation if form has data
      closeConfirm.showConfirm(() => {
        resetForm();
        onClose(false);
      });
    } else {
      // No data, just close
      resetForm();
      onClose(false);
    }
  };

  // Validation states
  const [titleValidation, setTitleValidation] = useState({ isValid: true, message: '' });
  const [dateValidation, setDateValidation] = useState({ isValid: true, message: '' });
  const [startTimeValidation, setStartTimeValidation] = useState({ isValid: true, message: '' });
  const [endTimeValidation, setEndTimeValidation] = useState({ isValid: true, message: '' });
  const [locationValidation, setLocationValidation] = useState({ isValid: true, message: '' });
  const [meetingTypeValidation, setMeetingTypeValidation] = useState({ isValid: true, message: '' });
  const [participantsValidation, setParticipantsValidation] = useState({ isValid: true, message: '' });
  const [agendaValidation, setAgendaValidation] = useState({ isValid: true, message: '' });
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [documentUploadTask, setDocumentUploadTask] = useState(null);
  const [showAgendaUploadModal, setShowAgendaUploadModal] = useState(false);
  const [agendaFiles, setAgendaFiles] = useState([]);
  const [showCustomRecurrenceModal, setShowCustomRecurrenceModal] = useState(false);

  // Sync allUsers with props users when modal opens OR users data changes
  useEffect(() => {
    if (open && users.length > 0) {
      setAllUsers(users);
    }
  }, [open, users.length]); // Sync when modal opens OR users are loaded

  // Memoize all users (for displaying already selected participants, even if disabled)
  const allAvailableUsers = useMemo(() => allUsers, [allUsers]);

  // Memoize ACTIVE users only (for new selections)
  // All users can see and select all active users as participants
  // Restriction is only on who can CREATE new users (handled in AddParticipantModal)
  const activeUsers = useMemo(() => {
    return allUsers.filter(u => {
      const isActive = u.is_active === true || u.is_active === undefined;
      const notDeleted = !u.is_deleted;
      return isActive && notDeleted;
    });
  }, [allUsers.length, allUsers.map(u => u.id + u.is_active).join(',')]);

  // Filter users: show frequent participants by default, selected, or search results
  const filteredUsers = useMemo(() => {
    // If no search query
    if (!searchQuery.trim()) {
      // Show selected participants (may include disabled users)
      const selectedUsers = allAvailableUsers.filter(u => formData.participant_ids.includes(u.id));

      // If no participants selected, show frequent participants (top 5-6)
      if (selectedUsers.length === 0 && frequentParticipants.length > 0) {
        return frequentParticipants;
      }

      return selectedUsers;
    }

    // If searching, show ALL ACTIVE matching users (no pagination, all in one list)
    const query = searchQuery.toLowerCase();

    // Search in active users only (employees, business heads, vendors)
    const matches = activeUsers.filter(user => {
      const name = (user.full_name || user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });

    return matches;
  }, [allAvailableUsers, activeUsers, searchQuery, formData.participant_ids, frequentParticipants]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData(prev => ({ ...prev, title: value }));
      setTitleValidation(validateMeetingTitle(value));
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData(prev => ({ ...prev, location: value }));
      // Only validate location if meeting type is in-person
      if (formData.meeting_type === 'in-person') {
        setLocationValidation(validateLocation(value));
      } else {
        setLocationValidation({ isValid: true, message: '' });
      }
    }
  };

  const handleMeetingTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      meeting_type: value,
      // Clear location when switching to online
      location: value === 'online' ? '' : prev.location
    }));
    setMeetingTypeValidation(validateMeetingType(value));
    // Reset location validation when switching to online (location not required)
    if (value === 'online') {
      setLocationValidation({ isValid: true, message: '' });
    } else if (value === 'in-person' && formData.location) {
      // Re-validate location when switching to in-person
      setLocationValidation(validateLocation(formData.location));
    }
  };



  const handleDateChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, scheduled_date: value }));
    const validation = validateDate(value, 'Meeting date');
    setDateValidation(validation);

    // Re-validate start time if date changes and start time is set
    if (formData.start_time) {
      const startTimeValidationResult = validateStartTime(formData.start_time, formData.end_time, value);
      setStartTimeValidation(startTimeValidationResult);
    }
  };

  const handleTimeChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Validate individual time fields separately
    if (field === 'start_time') {
      const startTimeValidationResult = validateStartTime(newFormData.start_time, newFormData.end_time, newFormData.scheduled_date);
      setStartTimeValidation(startTimeValidationResult);

      // If start time is valid and end time exists, re-validate end time
      // This ensures the error shows on end time if start time is after end time
      if (newFormData.end_time && startTimeValidationResult.isValid) {
        const endTimeValidationResult = validateEndTime(newFormData.end_time, newFormData.start_time);
        setEndTimeValidation(endTimeValidationResult);
      } else if (newFormData.end_time && newFormData.start_time >= newFormData.end_time) {
        // If start time is after end time, clear start time error and show error on end time
        setStartTimeValidation({ isValid: true, message: '' });
        setEndTimeValidation({ isValid: false, message: 'End time must be after start time' });
      }
    } else if (field === 'end_time') {
      const endTimeValidationResult = validateEndTime(newFormData.end_time, newFormData.start_time);
      setEndTimeValidation(endTimeValidationResult);

      // If end time is valid and start time exists, re-validate start time
      if (newFormData.start_time && endTimeValidationResult.isValid) {
        const startTimeValidationResult = validateStartTime(newFormData.start_time, newFormData.end_time, newFormData.scheduled_date);
        setStartTimeValidation(startTimeValidationResult);
      }
    }
  };

  const handleParticipantChange = (userId, checked) => {
    const newParticipantIds = checked
      ? [...formData.participant_ids, userId]
      : formData.participant_ids.filter(id => id !== userId);

    setFormData(prev => ({ ...prev, participant_ids: newParticipantIds }));
    setParticipantsValidation(validateParticipants(newParticipantIds));
  };


  const handleAgendaChange = (htmlContent) => {
    // QuillEditor passes HTML content directly - no character limit
    setFormData(prev => ({ ...prev, agenda: htmlContent }));
    setAgendaValidation(validateAgenda(htmlContent));
  };


  const recurrenceOptions = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom' }
  ];

  // Load frequent participants when modal opens
  useEffect(() => {
    if (open) {
      console.log('🔍 Loading frequent participants...');
      const loadFrequentParticipants = async () => {
        try {
          const response = await userAPI.getFrequentParticipants();
          console.log('✅ Frequent participants loaded:', response.data);
          setFrequentParticipants(response.data || []);
        } catch (error) {
          // Silent fail - not critical, just show empty list
          console.log('❌ No frequent participants found or error loading them:', error);
          setFrequentParticipants([]);
        }
      };
      loadFrequentParticipants();
    } else {
      console.log('Modal closed, resetting frequent participants');
    }
  }, [open]);

  // Load ongoing meetings when modal opens
  useEffect(() => {
    if (open) {
      const loadOngoingMeetings = async () => {
        try {
          // OPTIMIZED: Fetch only ongoing meetings from backend instead of all meetings
          const response = await meetingAPI.getMeetings({
            state: 'ongoing',  // Backend filter for ongoing meetings only
            page_size: 100     // Reasonable limit for ongoing meetings
          });

          // Backend already filters by state=ongoing, just use the data directly
          const ongoingMeetings = response.data?.items || response.data || [];
          setOngoingMeetings(ongoingMeetings);
        } catch (error) {
          handleAPIError(error, 'Failed to load ongoing meetings', false);
        }
      };
      loadOngoingMeetings();

      // Refresh ongoing meetings every 30 seconds
      const interval = setInterval(loadOngoingMeetings, 30000);
      return () => clearInterval(interval);
    } else {
      // Reset search query and frequent participants when modal closes
      setSearchQuery('');
      setFrequentParticipants([]);
    }
  }, [open]);

  // Check for conflicts whenever form data changes
  useEffect(() => {
    if (formData.scheduled_date && formData.start_time && formData.end_time && formData.participant_ids.length > 0) {
      const newConflicts = findConflictingParticipants(
        formData.participant_ids,
        ongoingMeetings,
        formData.scheduled_date,
        formData.start_time,
        formData.end_time,
        allAvailableUsers
      );
      setConflicts(newConflicts);
    } else {
      setConflicts([]);
    }
  }, [formData.scheduled_date, formData.start_time, formData.end_time, formData.participant_ids, ongoingMeetings, allAvailableUsers]);

  // Handler when new user (employee/vendor) is created
  const handleUserCreated = (newUser) => {
    // Add to allUsers list
    setAllUsers(prev => [...prev, newUser]);
    // Auto-select the newly created user
    setFormData(prev => ({ ...prev, participant_ids: [...prev.participant_ids, newUser.id] }));
    // Clear search
    setSearchQuery('');
    showToast.success(`${newUser.full_name} added to participants`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate all fields
      const titleValidationResult = validateMeetingTitle(formData.title);
      const locationValidationResult = validateLocation(formData.location);
      const meetingTypeValidationResult = validateMeetingType(formData.meeting_type);
      const dateValidationResult = validateDate(formData.scheduled_date, 'Meeting date');
      const startTimeValidationResult = validateStartTime(formData.start_time, formData.end_time, formData.scheduled_date);
      const endTimeValidationResult = validateEndTime(formData.end_time, formData.start_time);
      const participantsValidationResult = validateParticipants(formData.participant_ids);
      const agendaValidationResult = validateAgenda(formData.agenda);

      if (!titleValidationResult.isValid) {
        setTitleValidation(titleValidationResult);
        showToast.error(titleValidationResult.message);
        setIsLoading(false);
        return;
      }

      // Only validate location if meeting type is in-person
      if (formData.meeting_type === 'in-person' && !locationValidationResult.isValid) {
        setLocationValidation(locationValidationResult);
        showToast.error(locationValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!meetingTypeValidationResult.isValid) {
        setMeetingTypeValidation(meetingTypeValidationResult);
        showToast.error(meetingTypeValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!dateValidationResult.isValid) {
        setDateValidation(dateValidationResult);
        showToast.error(dateValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!startTimeValidationResult.isValid) {
        setStartTimeValidation(startTimeValidationResult);
        showToast.error(startTimeValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!endTimeValidationResult.isValid) {
        setEndTimeValidation(endTimeValidationResult);
        showToast.error(endTimeValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!participantsValidationResult.isValid) {
        setParticipantsValidation(participantsValidationResult);
        showToast.error(participantsValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!agendaValidationResult.isValid) {
        setAgendaValidation(agendaValidationResult);
        showToast.error(agendaValidationResult.message);
        setIsLoading(false);
        return;
      }

      // NOTE: Conflicts are now informational only - users can proceed even with conflicts
      // Conflicts are displayed as warnings in the UI but don't block meeting creation

      if (formData.participant_ids.length === 0) {
        showToast.error('At least one participant must be selected');
        return;
      }

      // Prepare meeting data (always JSON)
      const meetingData = {
        title: formData.title.trim(),
        scheduled_date: formData.scheduled_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location.trim(),
        meeting_type: formData.meeting_type,
        recurrence: formData.recurrence,
        participant_ids: formData.participant_ids,
        agenda: formData.agenda || null  // Send HTML content as-is
      };

      // Add custom recurrence pattern if recurrence is 'custom'
      if (formData.recurrence === 'custom' && formData.custom_recurrence) {
        // Convert frontend format to backend format
        // Map: day -> daily, week -> weekly, month -> monthly, year -> yearly
        const frequencyMap = {
          'day': 'daily',
          'week': 'weekly',
          'month': 'monthly',
          'year': 'yearly'
        };

        // Map: on -> on_date (frontend to backend)
        const endTypeMap = {
          'never': 'never',
          'on': 'on_date',
          'after': 'after'
        };

        const customPattern = {
          frequency: frequencyMap[formData.custom_recurrence.repeatUnit] || formData.custom_recurrence.repeatUnit,
          interval: formData.custom_recurrence.repeatEvery,
          end_type: endTypeMap[formData.custom_recurrence.endType] || formData.custom_recurrence.endType,
          end_value: formData.custom_recurrence.endType === 'on'
            ? formData.custom_recurrence.endDate
            : formData.custom_recurrence.endType === 'after'
              ? formData.custom_recurrence.occurrences
              : null,
          days_of_week: (formData.custom_recurrence.selectedDays && formData.custom_recurrence.selectedDays.length > 0)
            ? formData.custom_recurrence.selectedDays
            : null,
          day_of_month: null // Can be added for monthly patterns if needed
        };

        meetingData.custom_recurrence_pattern = customPattern;
      }

      // Create meeting first
      // Skip notification if there are documents to upload (will send after documents are uploaded)
      const hasDocuments = agendaFiles.length > 0;
      console.log('📤 Creating meeting with data:', meetingData);
      console.log(`📧 Skip notification: ${hasDocuments} (has ${agendaFiles.length} documents)`);

      const response = await onSubmit(meetingData, hasDocuments); // Pass skipNotification flag

      console.log('✅ Meeting created, response:', response);

      // Get created meeting ID from response
      const createdMeetingId = response?.data?.id || response?.id;

      console.log('📋 Created meeting ID:', createdMeetingId);
      console.log('📎 Agenda files to upload:', agendaFiles.length);

      // Upload agenda documents if any (one by one)
      if (agendaFiles.length > 0) {
        if (!createdMeetingId) {
          console.error('❌ No meeting ID found in response. Cannot upload documents.');
          showToast.error('Meeting created but documents could not be uploaded');
        } else {
          console.log(`📤 Uploading ${agendaFiles.length} agenda document(s)...`);
          let uploadedCount = 0;

          for (const file of agendaFiles) {
            try {
              console.log(`📤 Uploading: ${file.name}`);
              await meetingAPI.uploadAgendaDocument(createdMeetingId, file);
              uploadedCount++;
              console.log(`✅ Uploaded: ${file.name}`);
            } catch (error) {
              console.error(`❌ Failed to upload agenda document ${file.name}:`, error);
              showToast.error(`Failed to upload ${file.name}`);
            }
          }

          // Send notification AFTER documents are uploaded
          try {
            console.log('📧 Sending meeting notification with documents...');
            await meetingAPI.sendNotification(createdMeetingId, false); // isUpdate=false
            console.log('✅ Meeting notification sent successfully');
          } catch (error) {
            console.error('❌ Failed to send meeting notification:', error);
            // Don't show error to user - meeting is still created successfully
          }

          if (uploadedCount > 0) {
            showToast.success(`Meeting created with ${uploadedCount}/${agendaFiles.length} document(s) uploaded`);
          }
        }
      }

      {/*calling get meetings to refresh the ongoing meetings */ }
      window.dispatchEvent(new CustomEvent('meetingCreated'));

      // Reset form
      resetForm();

    } catch (error) {
      handleAPIError(error, 'Failed to create meeting', true);
    } finally {
      setIsLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[600px] w-[95vw] mx-auto max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="flex items-center gap-2">
                  {/* <Plus className="w-5 h-5 text-blue-600" /> */}
                  Create New Meeting
                </DialogTitle>
                <DialogDescription className="pt-3">
                  Schedule a new meeting with participants and agenda
                </DialogDescription>
              </div>
              <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6  space-y-6">
            <div className="grid grid-cols-1 gap-3">
              {/* Meeting Title */}
              <div>
                <FormField
                  label="Meeting Title"
                  type="text"
                  value={formData.title}
                  onChange={handleTitleChange}
                  validation={titleValidation}
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter Meeting Title"
                  required
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.title.length}/100 characters
                </p>
              </div>

              {/* Location and Meeting Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Meeting Type"
                  type="select"
                  value={formData.meeting_type}
                  onChange={handleMeetingTypeChange}
                  validation={meetingTypeValidation}
                  placeholder="Select type"
                  options={[
                    { value: 'in-person', label: 'In-Person' },
                    { value: 'online', label: 'Online' }
                  ]}
                  required
                />

                <FormField
                  label="Location"
                  type="text"
                  value={formData.location}
                  onChange={handleLocationChange}
                  validation={formData.meeting_type === 'online' ? null : locationValidation}
                  placeholder="Enter location (max 100 characters)"
                  required={formData.meeting_type === 'in-person'}
                  disabled={formData.meeting_type === 'online'}
                  maxLength={100}
                />


              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Meeting Date"
                  type="date"
                  value={formData.scheduled_date}
                  onChange={handleDateChange}
                  validation={dateValidation}
                  required
                  className="min-w-0 "
                  min={getTodayDate()}

                />

                <div className="space-y-2">
                  <FormField
                    label="Recurrence"
                    type="select"
                    value={formData.recurrence}
                    onChange={(value) => {
                      if (value === 'custom') {
                        setShowCustomRecurrenceModal(true);
                      } else {
                        setFormData(prev => ({ ...prev, recurrence: value, custom_recurrence: null }));
                      }
                    }}
                    placeholder="Select recurrence"
                    options={recurrenceOptions}
                  />
                  {/* Show custom recurrence summary */}
                  {formData.recurrence === 'custom' && formData.custom_recurrence && (
                    <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                      ✓ Custom: Every {formData.custom_recurrence.repeatEvery} {formData.custom_recurrence.repeatUnit}
                      {formData.custom_recurrence.selectedDays?.length > 0 &&
                        ` on ${formData.custom_recurrence.selectedDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`
                      }
                      {formData.custom_recurrence.endType === 'after' && ` (${formData.custom_recurrence.occurrences} times)`}
                      {formData.custom_recurrence.endType === 'on' && formData.custom_recurrence.endDate && ` (until ${formData.custom_recurrence.endDate})`}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Start Time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleTimeChange('start_time', e.target.value)}
                  validation={startTimeValidation}
                  required
                  min={formData.scheduled_date === getTodayDate() ? new Date().toTimeString().slice(0, 5) : undefined}
                />

                <FormField
                  label="End Time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleTimeChange('end_time', e.target.value)}
                  validation={endTimeValidation}
                  required
                  min={formData.start_time || undefined}
                />
              </div>


              {/* Participants */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Participants <span className="text-red-500">*</span></Label>

                {/* Employee restriction info */}


                <Card className="border border-gray-200">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Select Participants ({formData.participant_ids.length} selected)
                    </CardTitle>


                  </CardHeader>
                  <CardContent className="py-3 px-4 space-y-3">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search participants by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchQuery && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                      {isLoadingUsers ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="text-sm text-gray-500">Loading users...</div>
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-4 space-y-3">
                          <div className="text-sm text-gray-500">
                            {searchQuery
                              ? 'No users found matching your search'
                              : 'No users available'}
                          </div>
                          {searchQuery && searchQuery.includes('@') && searchQuery.includes('.') && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowAddParticipantModal(true);
                                setEmailForNewParticipant(searchQuery.trim());
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add "{searchQuery}" as {currentUser?.role === 'employee' ? 'New Vendor' : 'New Participant'}
                            </Button>
                          )}
                        </div>
                      ) : (
                        filteredUsers.map(user => {
                          const availability = getParticipantAvailability(
                            user.id,
                            ongoingMeetings,
                            formData.scheduled_date,
                            formData.start_time,
                            formData.end_time
                          );

                          return (
                            <div key={user.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`participant-${user.id}`}
                                checked={formData.participant_ids.includes(user.id)}
                                onCheckedChange={(checked) => handleParticipantChange(user.id, checked)}
                              />
                              <Label
                                htmlFor={`participant-${user.id}`}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                <div className="flex flex-col gap-1 w-full">
                                  <div className="flex items-center gap-2">
                                    {user.full_name || user.name || user.email} ({user.email})

                                    {/* Show disabled badge for inactive users */}
                                    {user.is_active === false && (
                                      <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs border-gray-300">
                                        🔒 Disabled
                                      </Badge>
                                    )}

                                    {/* Show warning if participant is in another meeting (informational only) */}
                                    {!availability.isAvailable && formData.scheduled_date && formData.start_time && formData.end_time && (
                                      <div className="flex items-center gap-1 text-amber-600">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span className="text-xs font-semibold">In Another Meeting</span>
                                      </div>
                                    )}
                                  </div>
                                  {!availability.isAvailable && availability.conflictingMeetings && availability.conflictingMeetings.length > 0 && (
                                    <div className="text-xs text-amber-600 ml-6 space-y-0.5">
                                      {availability.conflictingMeetings.map((conflictMeeting, idx) => (
                                        <div key={idx} className="flex items-center gap-1">
                                          <span>• In "{conflictMeeting.title}"</span>
                                          <span className="text-gray-600">
                                            ({conflictMeeting.start_time} - {conflictMeeting.end_time})
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </Label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Conflicts Summary - Informational Warning Only */}
                {conflicts.length > 0 && (
                  <Card className="border border-amber-200 bg-amber-50">
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-4 h-4" />
                        Participants Already in Other Meetings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-3 px-4">
                      <div className="space-y-2">
                        <p className="text-xs text-amber-700 mb-2 italic">
                          Note: These participants are already scheduled in other meetings at the same time. You can still add them to this meeting.
                        </p>
                        {conflicts.map((conflict, index) => (
                          <div key={index} className="text-sm text-amber-700">
                            <div className="font-medium">{conflict.user?.full_name || conflict.user?.name || conflict.user?.email || 'Unknown user'}</div>
                            <div className="text-xs text-amber-600 ml-2">
                              Also in: {conflict.conflicts.map(c =>
                                `"${c.title}" (${formatTime(c.start_time)} - ${formatTime(c.end_time)})`
                              ).join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Agenda */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Agenda
                </Label>
                <QuillEditor
                  value={formData.agenda}
                  onChange={handleAgendaChange}
                  placeholder="Enter meeting agenda and topics to discuss..."

                />
                {agendaValidation && !agendaValidation.isValid && (
                  <p className="text-xs text-red-500 mt-1">
                    {agendaValidation.message}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAgendaUploadModal(true)}
                      className="flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {agendaFiles.length > 0
                        ? `Add More Documents (${agendaFiles.length} selected)`
                        : 'Upload Agenda Documents'}
                    </Button>
                  </div>
                  {agendaFiles.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600 font-medium">
                        Attached Documents ({agendaFiles.length}):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {agendaFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded border"
                          >
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate max-w-[200px]" title={file.name}>
                              {file.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setAgendaFiles(prev => prev.filter((_, i) => i !== index))}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </form>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Meeting'}
            </Button>
          </div>
        </DialogContent>

        {/* Add Participant Modal */}
        <AddParticipantModal
          open={showAddParticipantModal}
          onClose={() => {
            setShowAddParticipantModal(false);
            setEmailForNewParticipant('');
          }}
          searchedEmail={emailForNewParticipant}
          onUserCreated={handleUserCreated}
        />

        {/* Agenda Upload Modal */}
        <AgendaUploadModal
          open={showAgendaUploadModal}
          onClose={() => setShowAgendaUploadModal(false)}
          existingFiles={agendaFiles}
          onFileSelect={(files) => {
            setAgendaFiles(prev => [...prev, ...files]);
            showToast.success(`${files.length} document${files.length > 1 ? 's' : ''} attached successfully`);
          }}
        />

        {/* Custom Recurrence Modal */}
        <CustomRecurrenceModal
          open={showCustomRecurrenceModal}
          onClose={() => {
            setShowCustomRecurrenceModal(false);
            // If no custom recurrence was saved, reset to 'none'
            if (!formData.custom_recurrence) {
              setFormData(prev => ({ ...prev, recurrence: 'none' }));
            }
          }}
          initialData={formData.custom_recurrence}
          onSave={(customRecurrence) => {
            setFormData(prev => ({ ...prev, recurrence: 'custom', custom_recurrence: customRecurrence }));
            setShowCustomRecurrenceModal(false);
          }}
        />
      </Dialog>

      {/* Close Confirmation Dialog */}
      <ConfirmDialog
        open={closeConfirm.isOpen}
        onOpenChange={closeConfirm.setIsOpen}
        onConfirm={closeConfirm.handleConfirm}
        title="Discard Changes?"
        description="You have unsaved changes in this form. Are you sure you want to close without saving?"
        confirmText="Yes, Close"
        cancelText="Keep Editing"
        variant="warning"
        icon="warning"
      />
    </>
  );
};

export default CreateMeetingModal;